import type {
	FullCourseLevel,
	FullCourseOffering,
} from "@repo/sanity-utils/query-types";

export function filterByModality(
	courseOfferings: FullCourseOffering[],
	modalityId: string
) {
	return courseOfferings.filter(
		(offering) => offering.modality._id === modalityId
	);
}

/**
 * Whether an offering can be taken at the given campus.
 *
 * An offering with no campuses listed is available everywhere; online
 * modalities never list campuses. A null `campusId` means the visitor has no
 * campus selected yet, so nothing is treated as unavailable.
 */
export function isOfferingAvailableAtCampus(
	offering: FullCourseOffering,
	campusId: string | null
): boolean {
	if (!campusId) {
		return true;
	}

	const campuses = offering.campuses ?? [];
	if (campuses.length === 0) {
		return true;
	}

	return campuses.some((campus) => campus.slug?.current === campusId);
}

function uniqueSequenceLabels(offerings: FullCourseOffering[]) {
	return [
		...new Set(
			offerings.map(({sequenceLabel}) => sequenceLabel).filter(Boolean)
		),
	];
}

/**
 * Returns the offering to display in the CTA card.
 * - Versions sequence type → first offering (versions share a single CTA)
 * - Explicit key (user picked via SequenceSelection) → that offering
 * - 1 part or no parts → auto-picks first match for the modality
 * - ≥2 parts → null (SequenceSelection handles explicit selection)
 */
export function getActiveOffering({
	courseLevel,
	selectedModalityId,
	selectedOfferingKey,
}: {
	courseLevel: Pick<
		NonNullable<FullCourseLevel>,
		"courseOfferings" | "sequenceType"
	>;
	selectedModalityId: string | null;
	selectedOfferingKey: string | null;
}): FullCourseOffering | null {
	const {courseOfferings, sequenceType} = courseLevel;

	// Versions sequence type default to just use the first offering
	if (sequenceType === "versions") {
		return courseOfferings[0] ?? null;
	}

	// Next check if there is a selected offering key
	if (selectedOfferingKey) {
		return (
			courseOfferings.find(
				(offering) => offering._key === selectedOfferingKey
			) ?? null
		);
	}

	// Otherwise this is based on the selected modality
	if (!selectedModalityId) return null;

	const matching = filterByModality(courseOfferings, selectedModalityId);
	const labels = uniqueSequenceLabels(matching);

	if (labels.length >= 2) return null;

	if (labels.length === 1) {
		return (
			matching.find(({sequenceLabel}) => sequenceLabel === labels[0]) ?? null
		);
	}

	return matching[0] ?? null;
}

/** Returns one offering per unique sequenceLabel for the selected modality. */
export function getSequenceParts({
	courseOfferings,
	selectedModalityId,
}: {
	courseOfferings: FullCourseOffering[];
	selectedModalityId: string | null;
}): FullCourseOffering[] {
	if (!selectedModalityId) return [];

	const matching = filterByModality(courseOfferings, selectedModalityId);
	const seenLabels = new Set<string>();

	return matching.filter(({sequenceLabel}) => {
		if (!sequenceLabel || seenLabels.has(sequenceLabel)) return false;
		seenLabels.add(sequenceLabel);
		return true;
	});
}

/**
 * Slug form of a sequence label for `?part=` (lowercase, spaces to dashes).
 * e.g. "Part 1" → "part-1"
 */
export function toSequencePartSlug(label: string): string {
	return label.trim().toLowerCase().replace(/\s+/g, "-");
}

/**
 * Resolve an offering `_key` from a `?part=` slug for the given sequence parts.
 * Matches `toSequencePartSlug(sequenceLabel)` (e.g. "part-1" for "Part 1").
 */
export function findOfferingKeyForPart(
	parts: FullCourseOffering[],
	part: string | null | undefined
): string | null {
	if (!part || !parts.length) {
		return null;
	}

	const normalized = toSequencePartSlug(part);
	if (!normalized) {
		return null;
	}

	const match = parts.find(
		({sequenceLabel}) =>
			sequenceLabel != null && toSequencePartSlug(sequenceLabel) === normalized
	);
	return match?._key ?? null;
}

/** Sequence-label slug for an offering key, or null if unknown. */
export function findPartSlugForOfferingKey(
	courseOfferings: FullCourseOffering[],
	offeringKey: string | null | undefined
): string | null {
	if (!offeringKey) {
		return null;
	}
	const offering = courseOfferings.find(({_key}) => _key === offeringKey);
	return offering?.sequenceLabel
		? toSequencePartSlug(offering.sequenceLabel)
		: null;
}

/**
 * Resolve the offering key for the current course + modality.
 *
 * Looks up this course's saved selection only (parts are not shared across
 * courses). Priority:
 * 1. Saved offering key when it matches the current modality's parts
 * 2. Remap via preferred part slug when the key is for another modality
 * 3. URL `?part=` / `initialPart` as a fallback (first paint / no selection yet)
 *
 * URL params must not permanently override jotai after the user picks a part —
 * seed `?part=` into the atom once (see SequenceSelection) so deep links still
 * win over a stale session without locking the UI.
 */
export function resolveSelectedOfferingKey({
	courseId,
	courseOfferings,
	selectedModalityId,
	selectionsByCourse,
	initialPart = null,
}: {
	courseId: string;
	courseOfferings: FullCourseOffering[];
	selectedModalityId: string | null;
	selectionsByCourse: Record<
		string,
		{offeringKey: string | null; preferredPartSlug: string | null}
	>;
	initialPart?: string | null;
}): string | null {
	const parts = getSequenceParts({
		courseOfferings,
		selectedModalityId,
	});

	const selection = selectionsByCourse[courseId];
	if (
		selection?.offeringKey &&
		parts.some(({_key}) => _key === selection.offeringKey)
	) {
		return selection.offeringKey;
	}

	const fromPreferredPart = findOfferingKeyForPart(
		parts,
		selection?.preferredPartSlug ?? null
	);
	if (fromPreferredPart) {
		return fromPreferredPart;
	}

	const urlPart = initialPart?.trim() ? toSequencePartSlug(initialPart) : null;
	return findOfferingKeyForPart(parts, urlPart);
}
